import React, { useEffect, useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { Label, Form, FormGroup, Row, Col } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import CustomizerContext from "../../../_helper/Customizer";
import {
  getSuburbsByCity,
  checkRouteExists,
  createRoute,
  updateRoute,
} from "../../../Services/ServiceCity.service";
import { showAlert } from "../../../Services/Shared.service";

const SuburbForm = ({ cityId, editingRoute, toggleModal, onRouteSaved, fetchRoutes }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suburbsList, setSuburbsList] = useState([]);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(true);
  const { mix_background_layout } = useContext(CustomizerContext);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fromSuburb: null,
      fromKml: "",
      toSuburb: null,
      toKml: "",
      charge: "",
    },
  });

  const fromSuburbValue = watch("fromSuburb");
  const toSuburbValue = watch("toSuburb");

  const isFromNew = !!(
    fromSuburbValue &&
    (fromSuburbValue.__isNew__ ||
      !suburbsList.some((s) => s.suburbId === fromSuburbValue.value))
  );
  const isToNew = !!(
    toSuburbValue &&
    (toSuburbValue.__isNew__ ||
      !suburbsList.some((s) => s.suburbId === toSuburbValue.value))
  );

  useEffect(() => {
    if (cityId && !editingRoute) {
      setIsLoadingSuburbs(true);
      getSuburbsByCity(cityId)
        .then((res) => {
          if (res?.data?.data) {
            setSuburbsList(res.data.data);
          } else {
            setSuburbsList([]);
          }
        })
        .catch((err) => {
          showAlert("error", err.response?.data?.message || "Failed to load suburbs");
        })
        .finally(() => {
          setIsLoadingSuburbs(false);
        });
    }
  }, [cityId, editingRoute]);

  useEffect(() => {
    if (editingRoute) {
      reset({
        fromSuburb: {
          value: editingRoute.fromSuburbId || "",
          label: editingRoute.fromSuburbName || "",
        },
        toSuburb: {
          value: editingRoute.toSuburbId || "",
          label: editingRoute.toSuburbName || "",
        },
        charge: editingRoute.charge !== undefined ? editingRoute.charge : "",
      });
    } else {
      reset({
        fromSuburb: null,
        fromKml: "",
        toSuburb: null,
        toKml: "",
        charge: "",
      });
    }
  }, [editingRoute, reset]);

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);

    if (editingRoute) {
      // Edit mode: only update charge
      const body = {
        charge: parseFloat(formValues.charge),
      };

      try {
        const res = await updateRoute(cityId, editingRoute.otherChargesId, body);
        const updatedRoute = res?.data?.data || {
          ...editingRoute,
          ...body,
        };
        onRouteSaved(updatedRoute, true);
        showAlert("success", "Route charge updated successfully");
        toggleModal();
      } catch (err) {
        showAlert("error", err.response?.data?.message || "Failed to update route");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const { fromSuburb, toSuburb, fromKml, toKml, charge } = formValues;

    if (!fromSuburb || !toSuburb) {
      showAlert("error", "Source and destination suburbs are required");
      setIsSubmitting(false);
      return;
    }

    const isFromNewVal = !!(
      fromSuburb.__isNew__ ||
      !suburbsList.some((s) => s.suburbId === fromSuburb.value)
    );
    const isToNewVal = !!(
      toSuburb.__isNew__ ||
      !suburbsList.some((s) => s.suburbId === toSuburb.value)
    );

    // 1. Validation - check identical suburb selection
    if (!isFromNewVal && !isToNewVal && fromSuburb.value === toSuburb.value) {
      showAlert("error", "Source and destination suburbs cannot be the same");
      setIsSubmitting(false);
      return;
    }
    if (fromSuburb.label.trim().toLowerCase() === toSuburb.label.trim().toLowerCase()) {
      showAlert("error", "Source and destination suburbs cannot be the same");
      setIsSubmitting(false);
      return;
    }

    let fromPayload = {};
    if (isFromNewVal) {
      fromPayload = {
        name: fromSuburb.label.trim(),
      };
    } else {
      fromPayload = {
        suburbId: parseInt(fromSuburb.value, 10),
      };
    }

    let toPayload = {};
    if (isToNewVal) {
      toPayload = {
        name: toSuburb.label.trim(),
      };
    } else {
      toPayload = {
        suburbId: parseInt(toSuburb.value, 10),
      };
    }

    const requestPayload = {
      from: fromPayload,
      to: toPayload,
      charge: parseFloat(charge),
    };
    
    const requestBlob = new Blob([JSON.stringify(requestPayload)], { type: 'application/json' });
    
    const formData = new FormData();
    formData.append("request", requestBlob);

    // Handle "from" payload file
    if (isFromNewVal && formValues.fromKml && formValues.fromKml.length > 0 && typeof formValues.fromKml !== "string") {
      formData.append("fromKmlFile", formValues.fromKml[0]);
    }

    // Handle "to" payload file
    if (isToNewVal && formValues.toKml && formValues.toKml.length > 0 && typeof formValues.toKml !== "string") {
      formData.append("toKmlFile", formValues.toKml[0]);
    }

    try {
      await createRoute(cityId, formData);
      showAlert("success", "Route created successfully");
      fetchRoutes();
      toggleModal();
    } catch (err) {
      showAlert("error", err.response?.data?.message || "Failed to create route");
    } finally {
      setIsSubmitting(false);
    }
  };

  const options = suburbsList.map((suburb) => ({
    value: suburb.suburbId,
    label: suburb.name,
  }));

  const selectStyles = {
    singleValue: (base) => ({
      ...base,
      color: mix_background_layout === "dark-only" ? "rgba(255, 255, 255, 0.7)" : null,
    }),
    valueContainer: (base) => ({
      ...base,
      width: "100%",
    }),
    control: (base, state) => ({
      ...base,
      backgroundColor:
        mix_background_layout === "dark-only"
          ? state.isDisabled
            ? "hsl(0, 0%, 75%)"
            : "#1d1e26"
          : null,
      borderColor:
        mix_background_layout === "dark-only"
          ? state.isFocused
            ? "#374558"
            : "#374558"
          : state.isFocused
          ? "rgb(38,132,255)"
          : "rgb(204, 204, 204)",
      boxShadow:
        mix_background_layout === "dark-only"
          ? state.isFocused
            ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
            : null
          : state.isFocused
          ? "0px 0px 0px 1px rgb(38,132,255)"
          : null,
      "&:hover": {
        borderColor:
          mix_background_layout === "dark-only"
            ? state.isFocused
              ? "#374558"
              : "#374558"
            : state.isFocused
            ? "rgb(38,132,255)"
            : "rgb(204, 204, 204)",
        boxShadow:
          mix_background_layout === "dark-only"
            ? state.isFocused
              ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
              : null
            : state.isFocused
            ? "0px 0px 0px 1px rgb(38,132,255)"
            : null,
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      svg: {
        fill: mix_background_layout === "dark-only" ? "rgba(255, 255, 255, 0.7)" : null,
      },
    }),
    menuList: (base) => ({
      ...base,
      color: mix_background_layout === "dark-only" ? "black" : null,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor:
        mix_background_layout === "dark-only"
          ? state.isSelected
            ? "#e0e0e0"
            : state.isFocused
            ? "#f0f0f0"
            : null
          : state.isSelected
          ? "rgba(65, 204, 125, 0.15)"
          : state.isFocused
          ? "#f8f9fa"
          : null,
      color:
        mix_background_layout === "dark-only"
          ? "black"
          : state.isSelected
          ? "#2b2b2b"
          : "inherit",
      "&:active": {
        backgroundColor:
          mix_background_layout === "dark-only"
            ? "#d0d0d0"
            : "rgba(65, 204, 125, 0.25)",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  if (editingRoute) {
    return (
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label style={{ fontWeight: "600" }}>From Suburb</Label>
              <input
                type="text"
                className="form-control"
                disabled
                value={editingRoute.fromSuburbName || ""}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label style={{ fontWeight: "600" }}>To Suburb</Label>
              <input
                type="text"
                className="form-control"
                disabled
                value={editingRoute.toSuburbName || ""}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <FormGroup>
              <Label style={{ fontWeight: "600" }}>Route Charge <span style={{ color: "red" }}>*</span></Label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                placeholder="e.g. 250.00"
                {...register("charge", {
                  required: "Charge amount is required",
                  min: { value: 0, message: "Charge cannot be negative" },
                })}
              />
              {errors.charge && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.charge.message}</span>
              )}
            </FormGroup>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2" style={{ marginTop: "20px" }}>
          <button
            type="button"
            className="btn btn-light"
            onClick={toggleModal}
            disabled={isSubmitting}
            style={{ textTransform: "none" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn"
            disabled={isSubmitting}
            style={{
              backgroundColor: "rgba(57, 181, 74, 1)",
              color: "white",
              textTransform: "none",
              fontWeight: "600",
            }}
          >
            {isSubmitting ? "Saving..." : "Update"}
          </button>
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        {/* From Suburb Column */}
        <Col md="6" style={{ borderRight: "1px solid #eee", paddingRight: "20px" }}>
          <h6 style={{ fontWeight: "700", marginBottom: "15px", color: "#626262" }}>From Suburb (Source)</h6>
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Select Suburb <span style={{ color: "red" }}>*</span></Label>
            {isLoadingSuburbs ? (
              <input className="form-control" type="text" disabled value="Loading suburbs..." />
            ) : (
              <Controller
                name="fromSuburb"
                control={control}
                rules={{ required: "Source suburb is required" }}
                render={({ field: { onChange, value, ref } }) => (
                  <CreatableSelect
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    options={options}
                    isClearable
                    placeholder="Search or Create Suburb"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                )}
              />
            )}
            {errors.fromSuburb && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.fromSuburb.message}</span>
            )}
          </FormGroup>

          {isFromNew && (
            <FormGroup>
              <Label style={{ fontWeight: "600" }}>KML Boundary File <span style={{ color: "red" }}>*</span></Label>
              <input
                type="file"
                accept=".kml,.xml"
                className="form-control"
                {...register("fromKml", {
                  required: "Source KML boundary file is required",
                })}
              />
              {errors.fromKml && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.fromKml.message}</span>
              )}
            </FormGroup>
          )}
        </Col>

        {/* To Suburb Column */}
        <Col md="6" style={{ paddingLeft: "20px" }}>
          <h6 style={{ fontWeight: "700", marginBottom: "15px", color: "#626262" }}>To Suburb (Destination)</h6>
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Select Suburb <span style={{ color: "red" }}>*</span></Label>
            {isLoadingSuburbs ? (
              <input className="form-control" type="text" disabled value="Loading suburbs..." />
            ) : (
              <Controller
                name="toSuburb"
                control={control}
                rules={{ required: "Destination suburb is required" }}
                render={({ field: { onChange, value, ref } }) => (
                  <CreatableSelect
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    options={options}
                    isClearable
                    placeholder="Search or Create Suburb"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                )}
              />
            )}
            {errors.toSuburb && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.toSuburb.message}</span>
            )}
          </FormGroup>

          {isToNew && (
            <FormGroup>
              <Label style={{ fontWeight: "600" }}>KML Boundary File <span style={{ color: "red" }}>*</span></Label>
              <input
                type="file"
                accept=".kml,.xml"
                className="form-control"
                {...register("toKml", {
                  required: "Destination KML boundary file is required",
                })}
              />
              {errors.toKml && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.toKml.message}</span>
              )}
            </FormGroup>
          )}
        </Col>
      </Row>

      <Row style={{ borderTop: "1px solid #eee", marginTop: "15px", paddingTop: "15px" }}>
        <Col md="12">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Route Charge <span style={{ color: "red" }}>*</span></Label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              placeholder="e.g. 250.00"
              {...register("charge", {
                required: "Charge amount is required",
                min: { value: 0, message: "Charge cannot be negative" },
              })}
            />
            {errors.charge && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.charge.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2" style={{ marginTop: "20px" }}>
        <button
          type="button"
          className="btn btn-light"
          onClick={toggleModal}
          disabled={isSubmitting}
          style={{ textTransform: "none" }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn"
          disabled={isSubmitting}
          style={{
            backgroundColor: "rgba(57, 181, 74, 1)",
            color: "white",
            textTransform: "none",
            fontWeight: "600",
          }}
        >
          {isSubmitting ? "Saving..." : "Create"}
        </button>
      </div>
    </Form>
  );
};

export default SuburbForm;
